import Link from 'components/atoms/Link';
import { BASE_URL } from 'constants/env';
import { maxQuery, minQuery, SizeMax, SizeMin } from "constants/media";
import { BreakPointSwiper } from "constants/swiper";
import useWindowSize from 'hooks/useWindowSize';
import React, { memo, ReactNode } from "react";
import styled, { css } from "styled-components/macro";
import { Navigation, Pagination, SwiperOptions } from "swiper";
import { Swiper } from 'swiper/react';
import { joinClassnames, mapMultiClassnames } from "utils/map-modifier";
import "./index.scss";

export interface SubBanner {
  src?: string;
  srcMobile?: string;
  alt?: string;
  link?: string;
}

interface HeroBannerProps {
  id?: string;
  slidesPerView?: number;
  children: ReactNode;
  autoplay?: { delay: number; disableOnInteraction: boolean };
  loop?: boolean;
  modifiers?: Modifiers;
  speed?: number;
  pagination?: boolean;
  className?: string;
  centeredSlides?: boolean;
  mousewheel?: boolean;
  autoHeight?: boolean;
  activeSlideKey?: string;
  hideNavigationAt?: {
    min?: SizeMin;
    max?: SizeMax;
  };
  breakpoints?: BreakPointSwiper;
  subBanners?: Array<SubBanner>
}

const HeroBanner: React.FC<HeroBannerProps> = ({
  id,
  slidesPerView = 1,
  children,
  autoplay = undefined,
  loop,
  modifiers,
  speed = 200,
  pagination,
  className,
  centeredSlides = false,
  mousewheel,
  autoHeight,
  hideNavigationAt,
  activeSlideKey,
  breakpoints,
  subBanners,
}) => {
  const { width } = useWindowSize();
  const MOBILE = 576;
  let settings = {
    // Provide Swiper class as props
    Swiper,
    // Add modules you need
    modules: [Navigation, Pagination],
    slidesPerView,
    watchOverflow: true,
    uniqueNavElements: true,
    lazy: true,
    loop,
    speed,
    autoplay,
    mousewheel,
    centeredSlides,
    slidesPerGroup: 1,
    spaceBetween: 10,
    observer: true,
    observeParents: true,
    navigation: true,
    pagination: {
      type: "bullets",
      clickable: true,
    },
    autoHeight,
    breakpoints,
    shouldSwiperUpdate: false,
    rebuildOnUpdate: false,
  } as SwiperOptions;

  const baseUrl = BASE_URL;

  return (
    <div
      id={id || ""}
      className={joinClassnames(
        mapMultiClassnames("m-heroBanner", modifiers),
        className,
      )}
    >
      <div className="m-heroBanner_left">
        {children && (
          <SwiperTyped
            hideNavigationAt={hideNavigationAt}
            className="m-heroBanner_wrap-slider wrap-slider"
          >
            <Swiper {...settings}>{children}</Swiper>
          </SwiperTyped>
        )}
      </div>
      {
        subBanners && subBanners.length > 0 && (
          <div className="m-heroBanner_right">
            {
              subBanners.map((bn, index) => (
                <div key={index.toString()} className="m-heroBanner_right_image">
                  <Link href={bn.link || ''} >
                    <img
                      src={width <= MOBILE ? bn.srcMobile ? `${baseUrl}${bn.srcMobile}` : ''
                        : bn.src ? `${baseUrl}${bn.src}` : ''}
                      alt={bn.alt || 'sub banner'}
                    />
                  </Link>
                </div>
              ))
            }
          </div>
        )
      }
    </div>
  );
};

type SwiperType = {
  hideNavigationAt?: { min?: SizeMin; max?: SizeMax };
};

const SwiperTyped = styled.div<SwiperType>`
  ${({ hideNavigationAt }) =>
    hideNavigationAt &&
    (hideNavigationAt.min
      ? minQuery(
        hideNavigationAt.min,
        css`
            .swiper-button-next,
            .swiper-button-prev {
              display: none;
            }
          `,
      )
      : hideNavigationAt.max
        ? maxQuery(
          hideNavigationAt.max,
          css`
            .swiper-button-next,
            .swiper-button-prev {
              display: none;
            }
          `,
        )
        : css``)}
`;

export default memo(HeroBanner);
